// Useful articles:
// https://kentcdodds.com/blog/application-state-management-with-react
// https://kentcdodds.com/blog/how-to-use-react-context-effectively
import { createContext, useMemo, useReducer, useContext } from "react"

const ADD_ADDON_TO_CART = "ADD_ADDON_TO_CART"
const SET_ADDONS_TO_CART = "SET_ADDONS_TO_CART"

const AddonCartContext = createContext()

function AddonCartReducer(state, action) {
  switch (action.type) {
    case SET_ADDONS_TO_CART: {
      return action.addons
    }
    case ADD_ADDON_TO_CART: {
      if (state.length == 0) {
        return [action.addon]
      }
      // Line items can't have multiple addons with same type
      // (Only one Gift Wrap and/or Only one Greeting Card)
      const selectedAddon = state.find(
        ({ lid, addon_type }) =>
          action.addon.lid === lid && addon_type === action.addon.addon_type
      )

      if (!selectedAddon) {
        return [...state, action.addon]
      }

      // Replace addon with the new one
      return [
        ...state.filter(
          ({ lid, addon_type }) =>
            !(
              action.addon.lid === lid && addon_type === action.addon.addon_type
            )
        ),
        action.addon,
      ]
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`)
    }
  }
}

const AddonCartProvider = (props) => {
  const [state, dispatch] = useReducer(AddonCartReducer, [])
  const value = useMemo(() => [state, dispatch], [state])

  return <AddonCartContext.Provider value={value} {...props} />
}

const useAddonCart = () => {
  const context = useContext(AddonCartContext)
  if (!context) {
    throw new Error("useAddonCart must be used within a AddonCartProvider")
  }
  const [state, dispatch] = context

  const addGiftWrap = (wrap, { added_from, addon_type, lid }) => {
    const addon_obj = {
      ...wrap,
      ...wrap.package, // TODO: Try to get rid of this line
      addon_type,
      custom_properties: {
        added_from,
      },
      lid,
    }
    dispatch({
      addon: addon_obj,
      type: ADD_ADDON_TO_CART,
    })
  }

  const addGreetingCard = (card, { added_from, addon_type, lid }) => {
    const addon_obj = {
      ...card,
      addon_type,
      custom_properties: {
        added_from,
        greeting_card_properties: {},
      },
      id: `${lid}-${addon_type}`,
      lid,
    }
    if (card.addon_code) {
      addon_obj.custom_properties.greeting_card_properties.theme =
        card.addon_code
    }
    if (card.recipient) {
      addon_obj.custom_properties.greeting_card_properties.recipient =
        card.recipient
    }
    if (card.sender) {
      addon_obj.custom_properties.greeting_card_properties.sender = card.sender
    }
    if (card.message) {
      addon_obj.custom_properties.greeting_card_properties.message =
        card.message
    }
    dispatch({
      addon: addon_obj,
      type: ADD_ADDON_TO_CART,
    })
  }

  const removeAllAddonsWithType = (line_item_id, addon_type) => {
    dispatch({
      addons: state.filter(
        (addon) =>
          !(line_item_id === addon.lid && addon_type === addon.addon_type)
      ),
      type: SET_ADDONS_TO_CART,
    })
  }

  return {
    addGiftWrap,
    addGreetingCard,
    addonItems: state,
    removeAllAddonsWithType: removeAllAddonsWithType,
  }
}

export { AddonCartProvider, useAddonCart }
